import customerIoLogo from '@/assets/images/logos/thumbnail-logo-navigation.svg';
import { useAuth } from '@/services/auth';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { WorkspaceDropdown } from '../top-navigation/components/workspace-selector';
import {
  Image,
  Box,
  Icon,
  PlainButton,
  Popover,
  Text,
  Tooltip,
  useSideNavState,
} from '@customerio/pluma-components/react';
import { clsx } from 'clsx';
import { mergeRefs } from 'react-merge-refs';

import { useCallback, useRef, useState } from 'react';
import * as styles from './workspace-selector.css';

export function WorkspaceSelector(props: {
  onLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => void;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isPopoverOpenRef = useRef(isPopoverOpen);
  isPopoverOpenRef.current = isPopoverOpen;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const _setIsTooltipOpen = useCallback(
    (isOpen: boolean) => {
      // Don't open tooltip if popover is open
      if (isOpen && isPopoverOpenRef.current) {
        return;
      }
      setIsTooltipOpen(isOpen);
    },
    [setIsTooltipOpen],
  );

  const _setIsPopoverOpen = useCallback(
    (isOpen: boolean) => {
      setIsPopoverOpen(isOpen);

      if (isOpen) {
        _setIsTooltipOpen(false);
      }
    },
    [_setIsTooltipOpen, setIsPopoverOpen],
  );

  const currentWorkspace = useCurrentWorkspace();

  const sideNavState = useSideNavState();
  const isSideNavCollapsed = sideNavState?.isCollapsed ?? false;

  const onWorkspaceLinkClick = async (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => {
    props.onLinkClick(event, opts);
    _setIsPopoverOpen(false);
  };

  const auth = useAuth();
  const logoForNav =
    auth.account?.logo_url !== '' && auth.account?.logo_url != null ? auth.account?.logo_url : customerIoLogo;

  return (
    <>
      <Tooltip
        content={currentWorkspace?.name ?? ''}
        isDisabled={!isSideNavCollapsed}
        isCustom={true}
        isOpen={isTooltipOpen}
        onOpenChange={_setIsTooltipOpen}
      >
        {(tooltipContext) => (
          <Popover isOpen={isPopoverOpen} onOpenChange={_setIsPopoverOpen} placement="bottom-start" withArrow={false}>
            {(context) => (
              <>
                <PlainButton
                  className={clsx(
                    styles.workspaceSelectorButton,
                    isSideNavCollapsed ? styles.workspaceSelectorButtonCollapsed : null,
                  )}
                  aria-label={`${currentWorkspace?.name} workspace`}
                  aria-description={`Click to open workspace selector.`}
                  ref={mergeRefs([tooltipContext.reference.setReference, context.reference.setReference])}
                  {...tooltipContext.reference.getReferenceProps(context.reference.getReferenceProps())}
                >
                  <Image src={logoForNav} alt="" width={24} height={24} flexShrink="0" />
                  <Box className={styles.workspaceSelectorButtonInner}>
                    <Text text="product-ps-active" truncate={true} flexGrow="1">
                      {currentWorkspace?.name}
                    </Text>

                    <Icon name="chevron-toggle" ml="auto" flexShrink="0" />
                  </Box>
                </PlainButton>
                <WorkspaceDropdown onWorkspaceLinkClick={onWorkspaceLinkClick} />
              </>
            )}
          </Popover>
        )}
      </Tooltip>
    </>
  );
}
